"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UpdateAadAppDriver = exports.actionName = void 0;
const tslib_1 = require("tslib");
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const axios_1 = tslib_1.__importDefault(require("axios"));
const typedi_1 = require("typedi");
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../../error/common");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const aadAppClient_1 = require("./utility/aadAppClient");
const buildAadManifest_1 = require("./utility/buildAadManifest");
const constants_1 = require("./utility/constants");
const aadManifestHelper_1 = require("./utility/aadManifestHelper");
const path_1 = tslib_1.__importDefault(require("path"));
const wrapUtil_1 = require("../util/wrapUtil");
exports.actionName = "aadApp/update"; // DO NOT MODIFY the name
const helpLink = "https://aka.ms/teamsfx-actions/aadapp-update";
// logic from src\component\resource\aadApp\aadAppManifestManager.ts
let UpdateAadAppDriver = class UpdateAadAppDriver {
    constructor() {
        this.description = (0, localizeUtils_1.getLocalizedString)(constants_1.descriptionMessageKeys.update);
        this.progressTitle = (0, localizeUtils_1.getLocalizedString)("driver.aadApp.progressBar.updateAadAppTitle");
    }
    async execute(args, context) {
        const wrapDriverContext = new wrapUtil_1.WrapDriverContext(context, exports.actionName, exports.actionName);
        const result = await this._run(args, wrapDriverContext);
        return result;
    }
    async _run(args, context) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const summaries = [];
        try {
            (_a = context.logProvider) === null || _a === void 0 ? void 0 : _a.info((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.startExecuteDriver, exports.actionName));
            const state = this.loadCurrentState();
            this.validateArgs(args);
            const aadAppClient = new aadAppClient_1.AadAppClient(context.m365TokenProvider, context.logProvider);
            let manifest = await (0, buildAadManifest_1.buildAadManifest)(context, args.manifestPath, args.outputFilePath, state);
            // MS Graph API does not allow adding new OAuth permissions and pre authorize it within one request
            // So split update Microsoft Entra app to two requests:
            // 1. If there's preAuthorizedApplications, remove it temporary and update Microsoft Entra app to create possible new permission
            if (aadManifestHelper_1.AadManifestHelper.isNewAADManifestSchema(manifest)) {
                context.addTelemetryProperties({ [constants_1.telemetryKeys.isNewAadSchema]: "true" });
                manifest = manifest;
                if (((_b = manifest.api) === null || _b === void 0 ? void 0 : _b.preAuthorizedApplications) &&
                    ((_c = manifest.api.preAuthorizedApplications) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                    const preAuthorizedApplications = manifest.api.preAuthorizedApplications;
                    manifest.api.preAuthorizedApplications = [];
                    await aadAppClient.updateAadApp(manifest);
                    manifest.api.preAuthorizedApplications = preAuthorizedApplications;
                }
            }
            else {
                context.addTelemetryProperties({ [constants_1.telemetryKeys.isNewAadSchema]: "false" });
                manifest = manifest;
                if (manifest.preAuthorizedApplications && manifest.preAuthorizedApplications.length > 0) {
                    const preAuthorizedApplications = manifest.preAuthorizedApplications;
                    manifest.preAuthorizedApplications = [];
                    await aadAppClient.updateAadApp(manifest);
                    manifest.preAuthorizedApplications = preAuthorizedApplications;
                }
            }
            // 2. Update Microsoft Entra app again with full manifest to set preAuthorizedApplications
            await aadAppClient.updateAadApp(manifest);
            const summary = (0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.successUpdateAadAppManifest, args.manifestPath, manifest.id);
            (_d = context.logProvider) === null || _d === void 0 ? void 0 : _d.info(summary);
            summaries.push(summary);
            (_e = context.logProvider) === null || _e === void 0 ? void 0 : _e.info((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.successExecuteDriver, exports.actionName));
            const manifestPath = path_1.default.isAbsolute(args.manifestPath)
                ? args.manifestPath
                : path_1.default.join(context.projectPath, args.manifestPath);
            void aadManifestHelper_1.AadManifestHelper.showWarningIfManifestIsOutdated(manifestPath, context.projectPath);
            return {
                result: (0, teamsfx_api_1.ok)(new Map(Object.entries(state) // convert each property to Map item
                    .filter((item) => item[1] && item[1] !== "") // do not return Map item that is empty
                )),
                summaries: summaries,
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                (_f = context.logProvider) === null || _f === void 0 ? void 0 : _f.error((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.failExecuteDriver, exports.actionName, error.displayMessage));
                return {
                    result: (0, teamsfx_api_1.err)(error),
                    summaries: summaries,
                };
            }
            if (axios_1.default.isAxiosError(error) &&
                error.response // If no response, treat as unhandled error first to understand the actual problem
            ) {
                const message = JSON.stringify(error.response.data);
                (_g = context.logProvider) === null || _g === void 0 ? void 0 : _g.error((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.failExecuteDriver, exports.actionName, message));
                if (error.response.status >= 400 && error.response.status < 500) {
                    return {
                        result: (0, teamsfx_api_1.err)(new common_1.HttpClientError(error, exports.actionName, message, helpLink)),
                        summaries: summaries,
                    };
                }
                else {
                    return {
                        result: (0, teamsfx_api_1.err)(new common_1.HttpServerError(error, exports.actionName, message)),
                        summaries: summaries,
                    };
                }
            }
            const message = JSON.stringify(error);
            (_h = context.logProvider) === null || _h === void 0 ? void 0 : _h.error((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.failExecuteDriver, exports.actionName, message));
            return {
                result: (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, exports.actionName)),
                summaries: summaries,
            };
        }
        finally {
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (typeof args.manifestPath !== "string" || !args.manifestPath) {
            invalidParameters.push("manifestPath");
        }
        if (typeof args.outputFilePath !== "string" || !args.outputFilePath) {
            invalidParameters.push("outputFilePath");
        }
        if (invalidParameters.length > 0) {
            throw new common_1.InvalidActionInputError(exports.actionName, invalidParameters, helpLink);
        }
    }
    loadCurrentState() {
        return {
            AAD_APP_ACCESS_AS_USER_PERMISSION_ID: process.env.AAD_APP_ACCESS_AS_USER_PERMISSION_ID,
        };
    }
};
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(exports.actionName, exports.actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext]),
    tslib_1.__metadata("design:returntype", Promise)
], UpdateAadAppDriver.prototype, "_run", null);
UpdateAadAppDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(exports.actionName) // DO NOT MODIFY the service name
], UpdateAadAppDriver);
exports.UpdateAadAppDriver = UpdateAadAppDriver;
//# sourceMappingURL=update.js.map